import api from "../../utils/api";

/**
 * NZSC-4296 — 매매 프리미엄 새 엔진 운영 화면(구 관리자). 서버는 신규 관리자(admin-next)가 쓰는 api-admin `/api/v1/premium-engine/*` 그대로.
 * 응답은 { statusCode?, data } 래퍼 — data 만 꺼낸다. 실패는 인터셉터가 넘긴 { status, data:{code,message} } 를 그대로 돌려
 * 화면이 신규 관리자와 같은 문구 규칙(utils/premiumEngine errorMessage)으로 보여 준다.
 * 액션은 { data } 또는 { error } 를 돌려준다(던지지 않음).
 */
const BASE = "/api/v1/premium-engine";

const unwrap = res => res?.data?.data;

async function call(fn) {
  try {
    return { data: unwrap(await fn()) };
  } catch (error) {
    return { error };
  }
}

export const state = {
  _premiumEngineSwitch: null,
  _premiumEngineDetail: null,
  _premiumOpsQueue: null,
  _premiumEngineList: null,
};

export const mutations = {
  setPremiumEngineSwitch(state, data) {
    state._premiumEngineSwitch = data;
  },
  setPremiumEngineDetail(state, data) {
    state._premiumEngineDetail = data;
  },
  setPremiumOpsQueue(state, data) {
    state._premiumOpsQueue = data;
  },
  setPremiumEngineList(state, data) {
    state._premiumEngineList = data;
  },
};

export const actions = {
  /** 교체 스위치 상태. on=null 은 읽기 실패·마지막 값 없음(화면은 꺼짐으로 본다 — 신규 관리자와 같음). */
  async acGetPremiumEngineSwitch({ commit }) {
    const r = await call(() => api.get(`${BASE}/switch`));
    commit("setPremiumEngineSwitch", r.data ?? { on: null, source: "unknown" });
    return r;
  },
  /** 구독번호로 엔진(v2) 구독 찾기 — 새 로직 목록은 v2 만 내려 준다(NZSC-4289). */
  async acFindPremiumEngineBySubscribeSeq(_, subscribeSeq) {
    const r = await call(() => api.get(`${BASE}/subscriptions`, { params: { q: String(subscribeSeq), size: 1 } }));
    return r.error ? r : { data: r.data?.items?.[0] ?? null };
  },
  /**
   * NZSC-4296: 새 로직 목록(새 엔진 구독) — 신규 관리자 목록과 같은 API·같은 조건(상태 8가지·표시 플래그·검색·페이지).
   * 응답 { items, total, page, size, counts{all,byState,opsOpen,transferCheck,issuePaused}, states }.
   */
  async acGetPremiumEngineList({ commit }, { states = [], flag = null, q = "", page = 1, size = 50 } = {}) {
    const r = await call(() =>
      api.get(`${BASE}/subscriptions`, {
        params: {
          state: states.length ? states.join(",") : undefined,
          flag: flag || undefined,
          q: (q || "").trim() || undefined,
          page,
          size,
        },
      }),
    );
    if (r.data) commit("setPremiumEngineList", r.data);
    return r;
  },
  async acGetPremiumEngineDetail({ commit }, id) {
    const r = await call(() => api.get(`${BASE}/subscriptions/${id}`));
    if (r.data) commit("setPremiumEngineDetail", r.data);
    return r;
  },
  async acGetPremiumEngineTimeline(_, { id, before, limit = 200 }) {
    return call(() => api.get(`${BASE}/subscriptions/${id}/timeline`, { params: { before, limit } }));
  },
  async acGetPremiumCompare(_, issueId) {
    return call(() => api.get(`${BASE}/issues/${issueId}/compare`));
  },
  async acGetPremiumOpsQueue({ commit }, { kind, page = 1, size = 50 }) {
    const r = await call(() => api.get(`${BASE}/ops`, { params: { kind: kind || undefined, page, size } }));
    if (r.data) commit("setPremiumOpsQueue", r.data);
    return r;
  },
  /** 운영 확인 결정 — 사유 필수(빈칸·공백만이면 서버가 400 — 정본 "사유를 적어야 저장됨"). */
  async acPremiumOpsDecide(_, { opsId, decision, version, reason }) {
    return call(() => api.post(`${BASE}/ops/${opsId}/decision`, { decision, version, reason: reason.trim() }));
  },
  /** 정보 보완: 빠진 항목만 기존 테이블에 쓰고 시작 판정 재실행. 사유 필수. */
  async acPremiumInfoFill(_, { opsId, uniqueNo, owners, version, reason }) {
    return call(() =>
      api.post(`${BASE}/ops/${opsId}/info-fill`, { uniqueNo, owners, version, reason: reason.trim() }),
    );
  },
  /** 멸실 운영 조치(취소 처리·새 고유번호 반영). 사유 필수. */
  async acPremiumExtinctAction(_, { subscriptionId, action, uniqueNo, reason }) {
    return call(() =>
      api.post(`${BASE}/subscriptions/${subscriptionId}/extinct-action`, { action, uniqueNo, reason: reason.trim() }),
    );
  },
  /** 리포트 생성용 등기부 문서의 새 서명 URL(누를 때마다 — 1시간 만료). */
  async acGetPremiumReportRegistryDoc(_, subscriptionId) {
    return call(() => api.get(`${BASE}/subscriptions/${subscriptionId}/report-registry-doc`));
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
